// ABOUTME: What a refresh of calendar records did — one type for every layer, so "nothing to do",
// ABOUTME: "the user navigated away" and "it failed" can never be mistaken for one another again.

/**
 * Refreshing these records has exactly four outcomes, and telling them apart is
 * what this type is for. They used to be encoded as `undefined | null |
 * { failed: [] } | throw`, and every layer read that differently: a chunk that
 * was never asked reported success, a query torn down by navigation reported a
 * server error to the user, and a swallowed rejection turned a failed re-read
 * into a successful one — which is how a deleted card could come back.
 *
 * - `refreshed` — a request went out and answered;
 * - `nothing-to-do` — nobody owns these records here (no window, no chunk
 *   covers them, no days matched). Not a failure: there was nothing to ask;
 * - `aborted` — the work was dropped, not answered: the query was torn down,
 *   the chunk was skipped, the screen went away. Nothing is wrong, and nothing
 *   is fresher than before;
 * - `failed` — a request was made and did not answer, naming the sources so a
 *   user-initiated refresh can say which.
 */
export type RefreshOutcome =
  | { kind: "refreshed" }
  | { kind: "nothing-to-do" }
  | { kind: "aborted" }
  | { kind: "failed"; sources: readonly string[] }

export const REFRESHED: RefreshOutcome = { kind: "refreshed" }
export const NOTHING_TO_DO: RefreshOutcome = { kind: "nothing-to-do" }
export const ABORTED: RefreshOutcome = { kind: "aborted" }

export const refreshFailed = (sources: readonly string[]): RefreshOutcome => ({ kind: "failed", sources })

/** Only a failure is worth telling the user about; the other three are quiet. */
export const isRefreshFailure = (outcome: RefreshOutcome): outcome is { kind: "failed"; sources: readonly string[] } =>
  outcome.kind === "failed"

export const refreshFailureSources = (outcome: RefreshOutcome): readonly string[] =>
  isRefreshFailure(outcome) ? outcome.sources : []

/**
 * The outcome of several refreshes taken together, worst first: anything that
 * failed makes the whole thing a failure, then any real refresh, then anything
 * that was dropped. All quiet means there was nothing to do.
 */
export function combineRefreshOutcomes(outcomes: readonly RefreshOutcome[]): RefreshOutcome {
  const sources = outcomes.flatMap(refreshFailureSources)
  if (sources.length > 0) return refreshFailed(sources)
  if (outcomes.some((outcome) => outcome.kind === "refreshed")) return REFRESHED
  if (outcomes.some((outcome) => outcome.kind === "aborted")) return ABORTED
  return NOTHING_TO_DO
}
